import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { store } from 'store';
import { setLogOut } from 'store/authSlice';
import { isOAuthBridgeRoute } from 'utilities/oauthBridge';

export const axiosClient = axios.create({
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

const httpClient = axiosClient;

export const BASE_API = process.env.REACT_APP_BASE_API;
export const BASE_API_POST = process.env.REACT_APP_BASE_API_POST;
export const BASE_API_ADMIN = process.env.REACT_APP_BASE_API_ADMIN;
export const BASE_API_INTERACTION = process.env.REACT_APP_BASE_API_INTERACTION;
export const BASE_API_NOTIFICATION = process.env.REACT_APP_BASE_API_NOTIFICATION;
export const BASE_API_CHAT = process.env.REACT_APP_BASE_API_CHAT;
export const CLOUDFLARE_TURNSTILE = process.env.REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY;

export const StaticGroupId = {
  groupId: 'e190d53a-6c34-42db-81b2-d3a694cbe73e'
};
const clearLocalStorage = () => {
  localStorage.removeItem('chats-last-tab')
  localStorage.removeItem('groups-last-tab')
  localStorage.removeItem('group-last-tab')
  localStorage.removeItem('last-group')
  localStorage.removeItem('user-tab-list')
}
async function axiosResponseErrorHandler(error: AxiosError) {
  const axiosRequest = error?.request;
  const axiosResponse = error?.response;
  const isAxiosResponseValid = (axiosRes: any) => axiosRes;
  const isAxiosRequestValid = (axiosReq: any) => axiosReq;
  console.log('Axios Response Error Handler', { axiosRequest, axiosResponse });
  if (
    isAxiosRequestValid(axiosRequest) &&
    isAxiosResponseValid(axiosResponse)
  ) {
    if (axiosResponse?.status === 401 || axiosResponse?.status === 403) {
      console.warn('[Axios] Auth error intercepted', {
        status: axiosResponse.status,
        url: error.config?.url,
        pathname: window.location.pathname,
        withCredentials: error.config?.withCredentials
      });
      if (isOAuthBridgeRoute()) {
        return Promise.reject(error);
      }
      // Guests have no session to expire. Redirecting them repeats the failing request.
      if (axiosResponse?.status === 401 && store.getState().userAuth.isLoggedIn) {
        clearLocalStorage();
        store.dispatch(setLogOut());
        window.location.href = '/';
      }
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
  // Always reject the error, even if request/response validation fails
  return Promise.reject(error);
}

async function generateFinalUrl(
  path: string,
  apiUrl?: string,
  admin?: boolean
): Promise<string> {
  const finalUrl: string = `${apiUrl || (admin ? BASE_API_ADMIN : BASE_API_POST)}/${path}`;
  return finalUrl;
}

axiosClient.interceptors.request.use(async (config) => {
  const userAuth = await store.getState().userAuth;
  const idToken = userAuth?.authToken;
  const tokenAuth = idToken;
  // if (!config.headers.Authorization && tokenAuth ) {
  //   config.headers.Authorization = tokenAuth;
  // }
  return config;
});

axiosClient?.interceptors.response.use(
  async (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    console.log('Axios error intercepted:', error);
    return await axiosResponseErrorHandler(error);
  }
);

export async function axiosGet<R = unknown>(
  path: string,
  config?: AxiosRequestConfig | undefined,
  apiUrl?: string
): Promise<AxiosResponse<R>> {
  const finalUrl = await generateFinalUrl(path, apiUrl);
  const finalConfig = config ?? {};
  return httpClient.get(finalUrl, {
    ...finalConfig
  });
}

export async function axiosPut<D = unknown, R = D>(
  path: string,
  data?: D,
  config?: AxiosRequestConfig | undefined,
  apiUrl?: string
): Promise<AxiosResponse<R>> {
  const finalUrl = await generateFinalUrl(path, apiUrl);
  const finalConfig = config ?? {};
  return httpClient.put(finalUrl, data, {
    ...finalConfig
  });
}

export async function axiosPost<D = unknown, R = D>(
  path: string,
  data?: D,
  config?: AxiosRequestConfig | undefined,
  apiUrl?: string
): Promise<AxiosResponse<R>> {
  const finalUrl = await generateFinalUrl(path, apiUrl);
  const finalConfig = config ?? {};
  return httpClient.post(finalUrl, data, {
    ...finalConfig
  });
}

export async function axiosPostFile<D = unknown, R = D>(
  path: string,
  data?: D,
  config?: AxiosRequestConfig | undefined,
  apiUrl?: string
): Promise<AxiosResponse<R>> {
  const finalUrl = await generateFinalUrl(path, apiUrl);
  const finalConfig = config ?? {};
  return httpClient.post(finalUrl, data, {
    ...finalConfig
  });
}

export async function axiosDelete<R = unknown>(
  path: string,
  config?: AxiosRequestConfig | undefined,
  apiUrl?: string
): Promise<AxiosResponse<R>> {
  const finalUrl = await generateFinalUrl(path, apiUrl);
  const finalConfig = config ?? {};
  return httpClient.delete(finalUrl, {
    ...finalConfig
  });
}

export async function axiosPatch<D = unknown, R = D>(
  path: string,
  data?: D,
  config?: AxiosRequestConfig | undefined,
  apiUrl?: string
): Promise<AxiosResponse<R>> {
  const finalUrl = await generateFinalUrl(path, apiUrl);
  const finalConfig = config ?? {};
  return httpClient.patch(finalUrl, data, {
    ...finalConfig
  });
}
